import { AxiosResponse } from 'axios';
import PrescriptionInterface, { PrescriptionItemInterface } from '../interfaces/prescription';
import api from './api';
import { getItem } from './localSorage';
import replaceHtmlEntites from './replaceHtmlEntites';

export interface FetchPrescriptionResponse {
  prescription: any;
}

export const fetch = (token: string): Promise<PrescriptionInterface> => {
  const uri = `/dispenser/prescription/${token}?token=${getItem('token')}`;

  return api.get(uri).then((response: AxiosResponse<FetchPrescriptionResponse>) => {
    return normalize(response.data);
  });
};

export const dispense = (prescription: PrescriptionInterface): Promise<PrescriptionInterface | string[]> => {
  const token = getItem('token');
  const uri = `/dispenser?token=${token}`;

  const checkedItems = prescription.items
    .filter((item: PrescriptionItemInterface) => item.checked)
    .map((item: PrescriptionItemInterface) => {
      return item.id;
    });

  const payload = {
    prescription_token: prescription.token,
    items: checkedItems,
  };

  return api.post(uri, payload).then(
    (response: AxiosResponse<FetchPrescriptionResponse>): PrescriptionInterface => {
      return normalize(response.data);
    },
    (error: any): Promise<string[]> => {
      const errors = Object.entries(error.response.data.errors).map(([field, err]) => {
        return err as string;
      });

      return Promise.reject(errors);
    },
  );
};

export const cancelDispensationItem = (id: number, prescriptionToken: string): Promise<PrescriptionInterface> => {
  const authToken = getItem('token');
  const payload = {
    prescription_token: prescriptionToken,
    dispensation_ids: [id],
  };
  const uri = `dispenser/cancel?token=${authToken}`;

  return api.post(uri, payload).then((response: AxiosResponse<FetchPrescriptionResponse>) => {
    return normalize(response.data);
  });
};

const normalize = (response: FetchPrescriptionResponse): PrescriptionInterface => {
  const { prescription } = response;

  const data = {
    id: prescription.id,
    token: prescription.token,
    config: prescription.config,
    sent_via: prescription.sent_via,
    signed: prescription.isSigned,
    doctor: {
      id: prescription.doctor.id,
      abbreviation: prescription.doctor.abbreviation,
      name: prescription.doctor.name,
      city: prescription.doctor.city,
      regional_code: prescription.doctor.regional_code,
      board_number: prescription.doctor.board_number,
      uf: prescription.doctor.uf,
    },
    prescription_date: prescription.prescription_date,
    created_at: prescription.created_at,
    document: prescription.document,
    patient: {
      name: prescription.patient.name,
      social_name: prescription.patient?.social_name,
    },
    items: normalizeItems(prescription.items),
  };

  return data;
};

const normalizeItems = (items: any): PrescriptionItemInterface[] => {
  const flattenItems: PrescriptionItemInterface[] = [
    ...items.customized.data,
    ...items.industrialized.data,
    ...items.manipulated.data,
  ];

  return flattenItems
    .filter((item) => {
      return item.original_quantity !== null;
    })
    .map((item: any) => {
      const dispensation = item.dispensation.data || [];

      return {
        id: item.id,
        name: item.compositions?.exist ? "Fórmula" : item.name,
        dosage: replaceHtmlEntites(item.dosage.replace(/(<([^>]+)>)/gi, '')),
        dispensed: dispensation[0]?.status === 'unknown',
        checked: false,
        blocked: dispensation[0]?.status === 'blocked',
        original_quantity: item.original_quantity,
        unit: item.unit,
        specialControl: item.special_control,
        recipe: item.recipe,
        compositions: item.compositions,
        dispensation: {
          data: dispensation.map((dispensationItem: any) => {
            return {
              id: dispensationItem.id,
              drugstore: dispensationItem.drugstore,
              // foi necessario realizar esse replace para compatibilidade com o ie11
              date: new Date(dispensationItem.created_at.replace(' ', 'T')),
              status: dispensationItem.status
            };
          }),
          document: {
            url: item.dispensation.document.url,
          },
        },
      };
    });
};

interface UnlockResponse {
  doctor: {
    name: string;
    crm: string;
    uf: string;
  };
  patient: {
    name: string;
  };
}

export const unlock = (digits: string, prescriptionToken: string): Promise<UnlockResponse> => {
  const uri = '/patients/prescriptions/clarify';

  return api
    .get(uri, {
      params: {
        digits,
        token: prescriptionToken,
      },
    })
    .then((response: AxiosResponse<UnlockResponse>) => response.data);
};
